import { atom, useAtom, useStore } from 'jotai';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { message, Modal } from 'antd';

export const editingCardsAtom = atom(new Map());
// shape: Map<string, { id: string, onSave: () => Promise<void>, onDiscard: () => void }>
// Multiple cards can be in edit mode simultaneously (e.g. two expanded cards on the same tab).

/**
 * Encapsulates card edit state and registers with the global editingCardsAtom
 * so that ProviderApplication.jsx can intercept tab changes with a save/discard modal.
 *
 * @param {object} options
 * @param {string}   options.id           - Unique identifier for this card (per page)
 * @param {Function} options.onSave       - Async fn that does the actual save work.
 *                                          Should throw on failure (including re-throwing API errors).
 *                                          form.validateFields() errors are caught silently.
 * @param {Function} [options.onDiscard]  - Called on cancel / modal "Discard All". Do form.resetFields()
 *                                          and any local state restoration here.
 *                                          If omitted, CardEdit will not render a Cancel button.
 * @param {Function} [options.onEditStart] - Called just before entering edit mode (e.g. form.setFieldsValue).
 *
 * @returns {{ isEditing, isSaving, editProps }}
 *   editProps spreads directly onto <CardEdit {...editProps} />
 */
export function useEditCard({ id, onSave, onDiscard, onEditStart }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [, setEditingCards] = useAtom(editingCardsAtom);

  // Keep latest callbacks in refs so stable useCallback handles don't go stale.
  const onSaveRef = useRef(onSave);
  const onDiscardRef = useRef(onDiscard);
  const onEditStartRef = useRef(onEditStart);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);
  useEffect(() => {
    onDiscardRef.current = onDiscard;
  }, [onDiscard]);
  useEffect(() => {
    onEditStartRef.current = onEditStart;
  }, [onEditStart]);

  const removeFromAtom = useCallback(() => {
    setEditingCards((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, [id, setEditingCards]);

  const handleCancel = useCallback(() => {
    onDiscardRef.current?.();
    setIsEditing(false);
    removeFromAtom();
  }, [removeFromAtom]);

  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);
      await onSaveRef.current();
      setIsEditing(false);
      removeFromAtom();
    } catch (err) {
      // Ant Design form validation errors have errorFields — stay in edit mode and scroll to first error.
      if (err?.errorFields) {
        // Scroll to first validation error
        const firstErrorField = err.errorFields[0]?.name;
        if (firstErrorField) {
          const fieldName = Array.isArray(firstErrorField) ? firstErrorField.join('_') : firstErrorField;
          const errorElement =
            document.querySelector(`[id="${fieldName}"]`) || document.querySelector(`[name="${fieldName}"]`);
          errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        throw err; // Re-throw to signal save failed
      }
      // Other errors: show error message and re-throw
      console.error(`[useEditCard] save error in card "${id}":`, err);
      message.error(`Failed to save changes. Please try again.`);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [id, removeFromAtom]);

  const handleEdit = useCallback(() => {
    onEditStartRef.current?.();
    setIsEditing(true);
    setEditingCards((prev) => new Map(prev).set(id, { id, onSave: handleSave, onDiscard: handleCancel }));
  }, [id, setEditingCards, handleSave, handleCancel]);

  // Keep the atom updated with latest callbacks while editing.
  useEffect(() => {
    if (isEditing) {
      setEditingCards((prev) => new Map(prev).set(id, { id, onSave: handleSave, onDiscard: handleCancel }));
    }
  }, [isEditing, id, handleSave, handleCancel, setEditingCards]);

  // Remove this card from the atom when it unmounts while editing.
  useEffect(() => {
    return () => removeFromAtom();
  }, [removeFromAtom]);

  return {
    isEditing,
    isSaving,
    editProps: {
      isEditing,
      onEdit: handleEdit,
      onSave: handleSave,
      // undefined → CardEdit renders no Cancel button (matches SchoolLetter / DocumentDetails)
      onCancel: onDiscard ? handleCancel : undefined,
      loading: isSaving,
    },
  };
}

/**
 * Blocks page-leave when any card is in edit mode and prompts the user to
 * save or discard changes first. Handles both in-app navigation (history.block)
 * and browser close / refresh (beforeunload).
 *
 * Drop this into any page component that hosts editable cards.
 */
export function useUnsavedChangesGuard() {
  const editingCardsAtomRef = useRef();
  const history = useHistory();
  const isProcessingRef = useRef(false);

  // Store atom getter in ref instead of subscribing to value
  const store = useStore();
  editingCardsAtomRef.current = () => store.get(editingCardsAtom);

  useEffect(() => {
    // Always register beforeunload so the browser shows its own "Leave site?" dialog.
    const handleBeforeUnload = (e) => {
      const editingCards = editingCardsAtomRef.current();
      if (editingCards.size > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Block in-app navigation (react-router v5).
    // Returning `false` from the block callback prevents the transition;
    // we then show our custom modal and replay the navigation after confirmation.
    const unblock = history.block((location, action) => {
      // Skip if we're already processing a save/discard
      if (isProcessingRef.current) return true;

      // Get fresh card references at modal time
      const editingCards = editingCardsAtomRef.current();
      const currentCards = [...editingCards.values()];
      const currentCount = currentCards.length;

      // Skip modal if no cards are in edit
      if (currentCount === 0) return true;

      Modal.confirm({
        title: 'You have unsaved changes',
        content: `You have ${currentCount} unsaved card${
          currentCount > 1 ? 's' : ''
        }. Would you like to save or discard ${currentCount > 1 ? 'them' : 'it'} before leaving?`,
        okText: currentCount > 1 ? 'Save all' : 'Save',
        cancelText: currentCount > 1 ? 'Discard all' : 'Discard',
        onOk: async () => {
          isProcessingRef.current = true;
          try {
            await Promise.all(currentCards.map((card) => card.onSave()));
            // All saves succeeded, navigate
            if (action === 'POP') history.go(-1);
            else if (action === 'REPLACE') history.replace(location);
            else history.push(location);
            // Small delay to ensure navigation completes before resetting
            setTimeout(() => {
              isProcessingRef.current = false;
            }, 100);
          } catch (err) {
            // If any save fails (validation or other error), stop navigation
            console.error('[useUnsavedChangesGuard] Save failed, staying on page:', err);
            isProcessingRef.current = false;
          }
        },
        onCancel: () => {
          isProcessingRef.current = true;
          try {
            currentCards.forEach((card) => card.onDiscard?.());
          } catch (err) {
            console.error('[useUnsavedChangesGuard] Error discarding cards:', err);
          }
          // Navigate while still processing, then reset
          if (action === 'POP') history.go(-1);
          else if (action === 'REPLACE') history.replace(location);
          else history.push(location);
          // Small delay to ensure navigation completes before resetting
          setTimeout(() => {
            isProcessingRef.current = false;
          }, 100);
        },
      });
      return false;
    });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      unblock();
    };
  }, [history]);
}
